import type { Invoice } from '@clockwise/clients/invoices';
import { ApiError, MAX_ADDITIONAL_SEND_RECIPIENTS, recipientAddresses } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Info, Loader2, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CcEmailsField } from '@/components/custom/cc-emails-field';
import { formatMoney } from '@/components/custom/invoice-money';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { invoicesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn, formatInstantDate, formatIsoDate } from '@/lib/utils';

/** What the dialog reads off the invoice — the editor's saved copy or a list row. */
export type SendableInvoice = Pick<
  Invoice,
  | 'id'
  | 'invoiceNumber'
  | 'billToName'
  | 'total'
  | 'currency'
  | 'invoiceDate'
  | 'status'
  | 'sentAt'
  | 'recipients'
>;

/** Approved invoices get their first send; sent ones may be sent again. */
export function canSendInvoice(status: string | null | undefined): boolean {
  return status === 'approved' || status === 'sent';
}

/**
 * Email one invoice now — from the editor or a row of the invoice list.
 *
 * Two independent choices, either or both: the invoice's own contacts (its
 * saved To and CC, listed so the sender sees who gets it), and additional
 * addresses for this send only, which are never saved to contacts. Only a
 * first send that includes the contacts marks the invoice sent; the server
 * decides that, and the toast reports what it did.
 *
 * Reads where the email will actually land from the API (`send-config`):
 * while the redirect guard is on, everything goes to the sender, and the
 * dialog says so rather than promising the customer a document.
 *
 * Errors stay in the dialog, beside the choice that caused them, so the
 * sender can fix the addresses and press Send again.
 */
export function InvoiceSendDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: SendableInvoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const contactsTo = recipientAddresses(invoice.recipients, 'to');
  const contactsCc = recipientAddresses(invoice.recipients, 'cc');
  const hasContacts = contactsTo.length > 0;
  const resend = invoice.status === 'sent';

  const [includeContacts, setIncludeContacts] = useState(hasContacts);
  const [includeAdditional, setIncludeAdditional] = useState(!hasContacts);
  const [additional, setAdditional] = useState<string[]>([]);
  /** Text still in the address input — an address not yet added. */
  const [pendingAddress, setPendingAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** One idempotency key per press: a retried request cannot send twice. */
  const [pressKey, setPressKey] = useState<string | null>(null);

  // Fresh each time it opens: last send's addresses are not this one's.
  useEffect(() => {
    if (!open) return;
    setIncludeContacts(hasContacts);
    setIncludeAdditional(!hasContacts);
    setAdditional([]);
    setPendingAddress('');
    setError(null);
    setPressKey(null);
  }, [open, hasContacts]);

  const config = useQuery({
    queryKey: queryKeys.invoices.sendConfig(),
    queryFn: () => invoicesApi.sendConfig(),
    enabled: open,
    staleTime: 60_000,
  });
  const redirectTo = config.data?.data.redirectToSender ? config.data.data.redirectTo : null;

  const send = useMutation({
    mutationFn: (key: string) =>
      invoicesApi.send(
        invoice.id,
        {
          includeContacts,
          additionalTo: includeAdditional ? additional : [],
        },
        { idempotencyKey: key },
      ),
    onMutate: () => setError(null),
    onSuccess: ({ delivery }) => {
      const n = delivery.deliveredTo.length;
      if (delivery.redirected) {
        toast.success(`Invoice ${invoice.invoiceNumber} sent to you only`, {
          description: `Test mode: it went to ${delivery.deliveredTo.join(', ')}, not the customer.`,
        });
      } else if (delivery.markedSent) {
        toast.success(`Invoice ${invoice.invoiceNumber} sent`);
      } else {
        toast.success(
          `Invoice ${invoice.invoiceNumber} ${resend ? 'resent' : 'emailed'} to ${n} ${n === 1 ? 'address' : 'addresses'}`,
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(invoice.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not send the invoice');
      // A failure the sender recorded comes back on the invoice ("Last send
      // failed"); refresh so it shows once the dialog closes.
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(invoice.id) });
    },
    onSettled: () => setPressKey(null),
  });

  const recipientsCount =
    (includeContacts ? contactsTo.length + contactsCc.length : 0) +
    (includeAdditional ? additional.length : 0);

  const onSend = () => {
    if (!includeContacts && !includeAdditional) {
      setError('Choose who gets the email.');
      return;
    }
    // Still in the input after its blur tried to add it, so not a valid
    // address. Sending without it would quietly leave someone out.
    if (includeAdditional && pendingAddress.trim() !== '') {
      setError(`“${pendingAddress.trim()}” isn’t a valid email address. Fix or clear it first.`);
      return;
    }
    if (includeAdditional && additional.length === 0) {
      setError('Add at least one email address, or untick Additional addresses.');
      return;
    }
    if (includeAdditional && additional.length > MAX_ADDITIONAL_SEND_RECIPIENTS) {
      setError(`Send to at most ${MAX_ADDITIONAL_SEND_RECIPIENTS} additional addresses at once.`);
      return;
    }
    const key = pressKey ?? crypto.randomUUID();
    setPressKey(key);
    send.mutate(key);
  };

  const total = formatMoney(invoice.total, invoice.currency);
  const busy = send.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Not mid-send: closing would hide the outcome of an email in flight.
        if (!busy) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="invoice-send-dialog">
        <DialogHeader>
          <DialogTitle>
            {resend ? 'Resend' : 'Email'} invoice {invoice.invoiceNumber}?
          </DialogTitle>
          <DialogDescription>
            {[invoice.billToName, total, `Invoice date ${formatIsoDate(invoice.invoiceDate)}`]
              .filter(Boolean)
              .join(' · ')}
          </DialogDescription>
        </DialogHeader>

        {redirectTo ? (
          <div
            role="note"
            data-testid="invoice-send-redirect-notice"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
          >
            <FlaskConical
              className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400"
              aria-hidden
            />
            <span>
              Test mode: this email goes only to you ({redirectTo}). The contacts and addresses
              below get nothing.
            </span>
          </div>
        ) : null}

        {resend && invoice.sentAt ? (
          <p className="text-muted-foreground text-sm">
            Sent {formatInstantDate(invoice.sentAt)}. Sending it again doesn’t change its status.
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <p className="font-medium text-sm">Send to</p>

          <label
            htmlFor="invoice-send-contacts-toggle"
            className={cn(
              'flex gap-3 rounded-md border p-3',
              includeContacts ? 'border-primary' : 'border-input',
              hasContacts ? 'cursor-pointer' : 'cursor-not-allowed opacity-70',
            )}
          >
            <Checkbox
              id="invoice-send-contacts-toggle"
              checked={includeContacts}
              disabled={!hasContacts || busy}
              onCheckedChange={(v) => {
                setIncludeContacts(v === true);
                setError(null);
              }}
              aria-label="Send to existing contacts"
              className="mt-0.5"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
              <span className="font-medium">Existing contacts</span>
              {hasContacts ? (
                <>
                  <RecipientLine label="To" addresses={contactsTo} />
                  {contactsCc.length > 0 ? (
                    <RecipientLine label="CC" addresses={contactsCc} />
                  ) : null}
                </>
              ) : (
                <span className="text-muted-foreground text-xs">
                  This invoice has no To contact. Add one on the invoice, or send to additional
                  addresses.
                </span>
              )}
            </div>
          </label>

          <div
            className={cn(
              'flex gap-3 rounded-md border p-3',
              includeAdditional ? 'border-primary' : 'border-input',
            )}
          >
            <Checkbox
              id="invoice-send-additional-toggle"
              checked={includeAdditional}
              disabled={busy}
              onCheckedChange={(v) => {
                setIncludeAdditional(v === true);
                setError(null);
              }}
              aria-label="Send to additional addresses"
              className="mt-0.5"
            />
            <div
              className="min-w-0 flex-1"
              // Typing an address is choosing to send to it. The input only:
              // removing a chip with its × is not.
              onFocusCapture={(e) => {
                if (e.target instanceof HTMLInputElement) setIncludeAdditional(true);
              }}
            >
              <CcEmailsField
                id="invoice-send-additional"
                label="Additional addresses"
                description="Additional emails to send the invoices"
                value={additional}
                disabled={busy}
                onDraftChange={setPendingAddress}
                onChange={(next) => {
                  // Adding an address chooses to send to it; removing one does not.
                  if (next.length > additional.length) setIncludeAdditional(true);
                  setAdditional(next);
                  setError(null);
                }}
              />
            </div>
          </div>
        </div>

        {invoice.status === 'approved' && !includeContacts && includeAdditional ? (
          <p className="flex items-start gap-2 text-muted-foreground text-sm">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
            Only additional addresses, so the invoice’s status doesn’t change.
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onSend} disabled={busy}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            {recipientsCount > 0
              ? `${resend ? 'Resend' : 'Send'} to ${recipientsCount}`
              : resend
                ? 'Resend'
                : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecipientLine({ label, addresses }: { label: string; addresses: string[] }) {
  return (
    <span className="flex gap-2">
      <span className="w-6 shrink-0 text-muted-foreground text-xs leading-5">{label}</span>
      <span className="min-w-0 break-words">{addresses.join(', ')}</span>
    </span>
  );
}
